import React, { useState } from "react";
import get from "lodash/get";
import map from "lodash/map";
import PropTypes from "prop-types";
import startsWith from "lodash/startsWith";
import cx from "classnames";
import { STRIPE } from "../constants";
import { getRoleNameById } from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import {
  calculateFreeTrialDaysLeft,
  isPullActiveCoach,
} from "../utils/user-helpers";

function SettingsNav(
  props,
  {
    locals: {
      pathname,
      activeUser,
      getCountryName,
      organizationRoles = [],
      organizationOwner,
      activeUserToRequest,
    },
  }
) {
  const [state, setState] = useState({});

  const isAthlete =
    !activeUser.permissions.includes("coach") && !activeUser.pushAccountInfo;

  //Get the name of the organization's role of the current user
  const roleName = !!activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
    : null;

  //Hide options if the user doesn't have a Owner role into the organization
  const hideContentForOrganizationsRolesBelowToOwner =
    !!activeUser.organizationRoleId && roleName !== "Owner";
  //Hide options if the user doesn't have a Owner or Admin role into the organization
  const hideContentForOrganizationsRolesBelowToAdmin =
    !!activeUser.organizationRoleId &&
    roleName !== "Admin" &&
    roleName !== "Owner";

  //TODO: come back and clean this up so PAGES is a const and we use lodash
  let PAGES = Object.assign(
    {
      "Account Setting": { url: "settings/profile", group: "general" },
      Preferences: { url: "settings/preferences", group: "general" },

      Branding: { url: "settings/branding", group: "general" },
      "Manage Coaches": { url: "settings/manage-coaches", group: "general" },
      Notifications: { url: "settings/notifications", group: "general" },
      FAQS: { url: "settings/faqs", group: "general" },
      "Gym Contract": { url: "settings/gym-contract", group: "gym" },
      "Gym Location": { url: "settings/gym-locations", group: "gym" },
      "Upgrade Account": { url: "settings/subscription", group: "general" },
      "Manage Billing": {
        url: "settings/coach-manage-billing",
        group: "billing",
      },
      "Manage Subscription": { url: "settings/subscription", group: "billing" },
      "Manage Coach": { url: "settings/manage-coaching", group: "general" },
      Billing: { url: "settings/billing", group: "billing" },
      "Coach Billing": { url: "settings/coach-billing", group: "billing" },
      Notifications: { url: "notifications", group: "general" },
    },
    isAthlete
      ? {
          "Exercise Library": {
            url: "settings/exercise-library?primary=squat",
            group: "general",
          },
        }
      : {}
  );

  const activeCoach = get(activeUser, "coach");
  const prospectiveCoach = get(activeUser, "coachingInvite.coach");
  const coach = activeCoach || prospectiveCoach;
  if (!coach) {
    delete PAGES["Manage Coach"];
  }
  const userIsCoach = activeUser.permissions.includes("coach");
  //We are checking if the user is member of an organization, if it's check the owner user, if not, check the current user
  const userIsFe =
    (!activeUser.organizationId && !organizationOwner) || !userIsCoach
      ? Boolean(activeUser.pushAccountInfo)
      : Boolean(organizationOwner.pushAccountInfo);
  const userIsPressCoach =
    (!activeUser.organizationId && !organizationOwner) || !userIsCoach
      ? Boolean(activeUser.pressAccountInfo)
      : Boolean(organizationOwner.pressAccountInfo);
  const userIsSubscriber = Boolean(activeUser.subscription);
  const plan = get(activeUser, "stripe.plan");
  const PLANS = STRIPE.PLANS;
  const userIsMember = Boolean(activeUser.membership);

  if (
    userIsCoach ||
    activeUser.admin ||
    activeUser.ambassador ||
    plan === PLANS.LAUNCH.trial ||
    plan === PLANS.V2.trial ||
    activeUser.cancelled ||
    activeCoach
  ) {
    delete PAGES["Billing"];
  }
  if (userIsCoach) {
    delete PAGES["Upgrade Account"];
    // delete PAGES["Notifications"];
    if (!userIsFe) {
      delete PAGES["Manage Subscription"];
      delete PAGES["FAQS"];
    }
  }

  if (!userIsCoach) {
    delete PAGES["FAQS"];
    delete PAGES["Manage Coaches"];
    delete PAGES["Manage Subscription"];
  }

  if (userIsSubscriber) {
    delete PAGES["Manage Coach"];
    delete PAGES["Notifications"];
    delete PAGES["Manage Subscription"];
    delete PAGES["Billing"];
  }
  if (userIsMember) {
    delete PAGES["Manage Coach"];
    delete PAGES["Manage Subscription"];
  }
  if (userIsCoach && !userIsFe) {
    delete PAGES["Manage Subscription"];
  }
  if (!userIsCoach && !userIsFe) {
    delete PAGES["Coach Billing"];
    delete PAGES["Manage Billing"];
    delete PAGES["Branding"];
    delete PAGES["Storefront Scripts"];
  }
  if (plan == PLANS.LAUNCH.trial || plan == PLANS.V2.trial) {
    delete PAGES["Manage Subscription"];
  }
  if ((plan !== PLANS.LAUNCH.trial && plan !== PLANS.V2.trial) || activeCoach) {
    delete PAGES["Upgrade Account"];
  }
  if (plan !== PLANS.LAUNCH.trial && plan !== PLANS.LAUNCH) {
    delete PAGES["Upgrade Your Account"];
  }
  if (!userIsPressCoach) {
    delete PAGES["Gym Contract"];
    delete PAGES["Gym Location"];
  }

  if (hideContentForOrganizationsRolesBelowToOwner) {
    delete PAGES["Upgrade Account"];
    delete PAGES["Manage Billing"];
    delete PAGES["Manage Subscription"];
    delete PAGES["Billing"];
    delete PAGES["Manage Coach"];
    delete PAGES["Coach Billing"];
  }

  if (hideContentForOrganizationsRolesBelowToAdmin) {
    delete PAGES["Upgrade Account"];
    delete PAGES["Manage Billing"];
    delete PAGES["Manage Subscription"];
    delete PAGES["Billing"];
    delete PAGES["Manage Coach"];
    delete PAGES["Coach Billing"];
    delete PAGES["FAQS"];
    delete PAGES["Branding"];
    delete PAGES["Gym Contract"];
    delete PAGES["Storefront Scripts"];
    delete PAGES["Gym Location"];
    delete PAGES["Manage Coaches"];
  }

  const daysLeft = calculateFreeTrialDaysLeft(activeUserToRequest);
  const isPullCoachOnTrial =
    activeUserToRequest.coachingFreeTrial && daysLeft > 0;
  const isPushCoachWithEndedTrial =
    activeUserToRequest.permissions.includes("coach") &&
    activeUserToRequest.pushAccountInfo &&
    daysLeft < 1 &&
    !isPullActiveCoach(activeUserToRequest);

  return (
    <aside role="secondary-navbar" tabIndex="-1" className={props.classNames}>
      {/* <div className="settings-box">
        <h6 className="mobile-hidden">Profile</h6>
        <div className="avatar-container mobile-hidden">
          <AvatarInput
            name="avatar"
            defaultValue={activeUser.avatar}
            imageSize={67}
          />

          <div className="profile-name">{activeUser.name}</div>
        </div>
      </div> */}
      <h4>General</h4>
      <ul>
        {startsWith(pathname, "/coaching-portal/")
          ? map(PAGES, (data, page) => {
              return data.group === "general" ? (
                <li
                  key={page}
                  className={cx({
                    active: `/coaching-portal/${data.url}` === pathname,
                  })}
                >
                  <a
                    className={cx({
                      clipped: `/coaching-portal/${data.url}` === pathname,
                    })}
                    href={`/coaching-portal/${data.url}`}
                  >
                    {page}
                  </a>
                  <img
                    className="settings-arrow only-show-mobile"
                    src="/img/svg/icon_arrow-right.svg"
                  />
                </li>
              ) : null;
            })
          : map(PAGES, (data, page) => {
              return data.group === "general" ? (
                <li
                  key={page}
                  className={cx({
                    active: `/dashboard/${data.url}` === pathname,
                  })}
                >
                  <a
                    className={cx({
                      active: `/dashboard/${data.url}` === pathname,
                    })}
                    href={`/dashboard/${data.url}`}
                  >
                    {page}
                  </a>
                </li>
              ) : null;
            })}
      </ul>
      {(isPullCoachOnTrial || isPushCoachWithEndedTrial) &&
        !hideContentForOrganizationsRolesBelowToOwner && <h4>Billing</h4>}
      <ul>
        {startsWith(pathname, "/coaching-portal/") ? (
          isPullCoachOnTrial || isPushCoachWithEndedTrial ? (
            <a href="/coach-billing" role="button" data-button-type="tertiary">
              <span>Upgrade Now</span>
            </a>
          ) : (
            map(PAGES, (data, page) => {
              const isBetaPage = page === "Manage Coaches";

              const liClassName = cx({
                "mobile-hidden": page === "Edit Profile" || page === "Branding",
                active: `/coaching-portal/${data.url}` === pathname,
              });

              return data.group === "billing" ? (
                <li key={page} className={liClassName}>
                  <a
                    className={cx({
                      active: `/coaching-portal/${data.url}` === pathname,
                    })}
                    href={`/coaching-portal/${data.url}`}
                  >
                    {page}
                    {isBetaPage && (
                      <span className="beta-settings-nav"> (BETA)</span>
                    )}
                  </a>
                  <img
                    className="settings-arrow only-show-mobile"
                    src="/img/svg/icon_arrow-right.svg"
                  />
                </li>
              ) : null;
            })
          )
        ) : (
          map(PAGES, (data, page) => {
            return data.group === "billing" ? (
              <li
                key={page}
                className={cx({
                  active: `/dashboard/${data.url}` === pathname,
                })}
              >
                <a
                  className={cx({
                    active: `/dashboard/${data.url}` === pathname,
                  })}
                  href={`/dashboard/${data.url}`}
                >
                  {page}
                </a>
              </li>
            ) : null;
          })
        )}
      </ul>
      {userIsPressCoach && <h4>Gym</h4>}
      <ul>
        {startsWith(pathname, "/coaching-portal/")
          ? map(PAGES, (data, page) => {
              const isBetaPage = page === "Manage Coaches";

              const liClassName = cx({
                "mobile-hidden": page === "Edit Profile" || page === "Branding",
                active: `/coaching-portal/${data.url}` === pathname,
              });

              return data.group === "gym" ? (
                <li key={page} className={liClassName}>
                  <a
                    className={cx({
                      active: `/coaching-portal/${data.url}` === pathname,
                    })}
                    href={`/coaching-portal/${data.url}`}
                  >
                    {page}
                    {isBetaPage && (
                      <span className="beta-settings-nav"> (BETA)</span>
                    )}
                  </a>
                  <img
                    className="settings-arrow only-show-mobile"
                    src="/img/svg/icon_arrow-right.svg"
                  />
                </li>
              ) : null;
            })
          : map(PAGES, (data, page) => {
              return data.group === "gym" ? (
                <li
                  key={page}
                  className={cx({
                    active: `/dashboard/${data.url}` === pathname,
                  })}
                >
                  <a
                    className={cx({
                      active: `/dashboard/${data.url}` === pathname,
                    })}
                    href={`/dashboard/${data.url}`}
                  >
                    {page}
                  </a>
                </li>
              ) : null;
            })}
      </ul>
    </aside>
  );
}

SettingsNav.contextTypes = { locals: PropTypes.object };

export default SettingsNav;
